<template>
  <div>
    <el-select
      ref="select"
      :value="value"
      :placeholder="placeholder"
      :size="size"
      :filterable="filterable"
      :remote="remoteFilter"
      :remote-method="remoteFilter ? remoteMethod : undefined"
      :clearable="clearable"
      :disabled="disabled"
      :multiple="multiple"
      @change="change($event)"
      @visible-change="handleVisibleChange"
      :style="vstyle"
      :class="vclass"
    >
      <el-option
        v-for="item in optionData"
        :key="options.value === 'this' ? item.value.value : item.value"
        :label="item.label"
        :value="item.value"
      ></el-option>
      <el-option
        v-if="loading"
        disabled
        :value="loadingOptionValue"
        label="加载中..."
      ></el-option>
    </el-select>
  </div>
</template>

<script>
import { request } from '@/api/service'

export default {
  name: 'dict-select-page',
  data() {
    return {
      optionData: [],
      svalue: '',
      selected: {},
      curPage: 1,
      pageSize: 20,
      pageTotal: 0,
      lastPageSize: 0,
      loading: false,
      scrollWrap: null,
      loadingOptionValue: '__dict_select_page_loading__',
      searchKeyword: '',
      remoteTimer: null,
    }
  },
  computed: {
    optionsMap() {
      return Object.fromEntries(this.optionData.map((o) => [o.value, o]))
    },
    hasMore() {
      if (this.pageTotal) {
        return this.optionData.length < this.pageTotal
      }
      return this.lastPageSize >= this.pageSize
    },
  },
  methods: {
    change(val) {
      this.$emit('input', val)
      this.$emit('change', val)
      this.$emit(
        'update:selectedLabel',
        this.optionsMap[val] ? this.optionsMap[val].label : ''
      )
      const selected = this.optionData.filter((item) => item.value === val)
      this.selected = selected && selected.length > 0 ? selected[0] : ''
    },
    init() {
      if (!this.url) return
      this.svalue = this.value
      this.reload(true)
    },
    reload(resetSearch = false) {
      if (resetSearch) {
        this.searchKeyword = ''
      }
      this.curPage = 1
      this.pageTotal = 0
      this.lastPageSize = 0
      this.fetchData(false)
    },
    remoteMethod(query) {
      this.searchKeyword = query || ''
      clearTimeout(this.remoteTimer)
      this.remoteTimer = setTimeout(() => {
        this.curPage = 1
        this.pageTotal = 0
        this.lastPageSize = 0
        this.fetchData(false)
      }, 300)
    },
    getRequestData() {
      const data = {
        ...this.body,
        curPage: this.curPage,
        pageSize: this.pageSize,
      }
      if (this.remoteFilter) {
        data[this.searchKey] = this.searchKeyword
      }
      return data
    },
    fetchData(isLoadMore = false) {
      this.loading = true
      request({
        url: this.url,
        method: this.methods,
        data: this.getRequestData(),
        params: this.params,
      })
        .then((res) => {
          const list = res.body.data || []
          const transformed = this._dataTransform(list)
          this.pageTotal = res.body.count || 0
          this.curPage = res.body.curPage || this.curPage
          this.lastPageSize = list.length
          if (isLoadMore) {
            const exist = new Set(this.optionData.map((o) => o.value))
            this.optionData.push(
              ...transformed.filter((o) => !exist.has(o.value))
            )
          } else {
            this.optionData = transformed
          }
          this.$emit('loaded', this.optionData)
          this.$emit('loadedOriginal', list)
        })
        .catch(() => {
          if (isLoadMore) {
            this.curPage = Math.max(1, this.curPage - 1)
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    loadMore() {
      if (!this.hasMore || this.loading) return
      this.curPage += 1
      this.fetchData(true)
    },
    handleVisibleChange(visible) {
      if (visible) {
        this.bindScroll()
        if (this.remoteFilter && !this.optionData.length) {
          this.reload(false)
        }
      } else {
        this.unbindScroll()
      }
    },
    bindScroll() {
      this.unbindScroll()
      this.$nextTick(() => {
        const selectRef = this.$refs.select
        if (!selectRef) return
        const popperEl = selectRef.popperElm || selectRef.$refs.popper?.$el
        const wrap = popperEl?.querySelector('.el-select-dropdown__wrap')
        if (!wrap) return
        this.scrollWrap = wrap
        this.scrollWrap.addEventListener('scroll', this.handleScroll)
      })
    },
    unbindScroll() {
      if (this.scrollWrap) {
        this.scrollWrap.removeEventListener('scroll', this.handleScroll)
        this.scrollWrap = null
      }
    },
    handleScroll(e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (scrollHeight - scrollTop - clientHeight <= 10) {
        this.loadMore()
      }
    },
    _dataTransform(data) {
      if (!this.options) {
        return data
      }
      const _data = data.map((t) => {
        const { label, value } = this.options
        return {
          label: t[label || 'label'],
          value: value === 'this' ? t : t[value],
        }
      })
      return _data || []
    },
  },
  watch: {
    svalue(val, oldVal) {
      if (val !== oldVal) {
        this.$emit('input', this.svalue)
      }
    },
    body(val) {
      if (val) {
        this.init()
      }
    },
    url(val) {
      if (val) {
        this.init()
      }
    },
  },
  props: {
    placeholder: {
      default: 'Please select',
      type: String,
    },
    size: {
      default: 'small',
      type: String,
    },
    vstyle: {
      default: 'width:100%;',
      type: String,
    },
    vclass: {
      default: '',
      type: String,
    },
    filterable: {
      default: false,
      type: Boolean,
    },
    remoteFilter: {
      default: false,
      type: Boolean,
    },
    searchKey: {
      default: 'processName',
      type: String,
    },
    clearable: {
      default: false,
      type: Boolean,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    multiple: {
      default: false,
      type: Boolean,
    },
    url: {
      default: '/master/common/dict/system',
      type: String,
    },
    value: {},
    selectedLabel: String,
    options: {
      type: Object,
      default: () => {
        return { label: 'label', value: 'value' }
      },
    },
    body: {
      type: Object,
    },
    params: {
      type: Object,
    },
    methods: {
      type: String,
      default: 'POST',
    },
  },
  mounted() {
    this.init()
  },
  beforeDestroy() {
    clearTimeout(this.remoteTimer)
    this.unbindScroll()
  },
}
</script>
